if (!customElements.get('short-videos-component')) {
  customElements.define('short-videos-component', class extends HTMLElement {
    constructor() {
      super();
      this.items = Array.from(this.querySelectorAll('.short-video-item'));
      this.grid = this.querySelector('.short-videos-grid');
      this.modal = this.querySelector('.short-video-modal');
      
      // Configurações baseadas em atributos
      this.onlyCenterAutoplay = this.getAttribute('data-autoplay-center') === 'true';
      this.isInfinite = this.getAttribute('data-infinite') === 'true';
      
      // No carrossel da Home inicia mudo, no Modal (produto) inicia desmutado
      this.isMuted = this.onlyCenterAutoplay; 
      
      this.defaultVolume = 0.8;
      this.isSectionVisible = false;
      this.progressAnimationFrame = null;
      
      this.init();
    }

    init() {
      if (this.isInfinite && this.grid && this.items.length > 0) {
        this.setupInfiniteLoop();
      }

      this.setupMuteButtons();
      this.setupNavigation();
      this.setupSectionObserver();

      if (this.modal) {
        this.setupModal();
      }
    }

    lazyLoadVideo(video) {
      if (!video.querySelector('source') && video.dataset.src) {
        const source = document.createElement('source');
        source.src = video.dataset.src;
        source.type = video.dataset.type;
        video.appendChild(source);
        video.load();
      }
    }

    setupLoadingIndicators(video) {
      if (video.dataset.loadingSetup) return;
      video.dataset.loadingSetup = 'true';

      // Indicador de pausa (apenas no modal do produto)
      const slide = video.closest('.short-video-slide');
      if (slide) {
        video.addEventListener('pause', () => { if (!this.isScrubbing) slide.classList.add('is-paused'); });
        video.addEventListener('play', () => slide.classList.remove('is-paused'));
      }

      const loader = video.parentElement.querySelector('.short-video-loading');
      if (!loader) return;
      const showLoader = () => { if (!video.paused) loader.classList.add('is-visible'); };
      const hideLoader = () => loader.classList.remove('is-visible');
      // Mostra o loader apenas quando o vídeo está bufferizando e deveria estar tocando
      video.addEventListener('waiting', showLoader);
      video.addEventListener('stalled', showLoader);
      // Esconde sempre que o vídeo está de fato reproduzindo/avançando ou parado
      video.addEventListener('playing', hideLoader);
      video.addEventListener('canplay', hideLoader);
      video.addEventListener('timeupdate', hideLoader);
      video.addEventListener('seeked', hideLoader);
      video.addEventListener('pause', hideLoader);
    }

    manageAutoplay() {
      const allVideos = this.querySelectorAll('.short-video-preview');
      if (!this.isSectionVisible) {
        allVideos.forEach(v => v.pause());
        return;
      }

      if (this.onlyCenterAutoplay && this.grid) {
        const gridRect = this.grid.getBoundingClientRect();
        const gridCenter = gridRect.left + gridRect.width / 2;
        let closestVideo = null;
        let minDistance = Infinity;

        allVideos.forEach(video => {
          const videoRect = video.getBoundingClientRect();
          const videoCenter = videoRect.left + videoRect.width / 2;
          const distance = Math.abs(gridCenter - videoCenter);
          if (distance < minDistance) {
            minDistance = distance;
            closestVideo = video;
          }
          video.pause();
        });

        if (closestVideo) {
          this.lazyLoadVideo(closestVideo);
          this.setupLoadingIndicators(closestVideo);
          closestVideo.muted = this.isMuted;
          closestVideo.volume = this.defaultVolume;
          closestVideo.play().catch(() => {});
        }
      } else {
        allVideos.forEach(video => {
          const rect = video.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
          if (isVisible && this.isSectionVisible) {
            this.lazyLoadVideo(video);
            this.setupLoadingIndicators(video);
            video.muted = true;
            video.volume = this.defaultVolume;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }
    }

    setupSectionObserver() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isSectionVisible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
          this.manageAutoplay();
        });
      }, { threshold: [0, 0.2] });
      observer.observe(this);

      if (this.grid) {
        this.grid.addEventListener('scroll', () => {
          requestAnimationFrame(() => this.manageAutoplay());
        }, { passive: true });
      }

      window.addEventListener('scroll', () => {
        requestAnimationFrame(() => {
            const rect = this.getBoundingClientRect();
            const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
            const visibility = visibleHeight / rect.height;
            const wasVisible = this.isSectionVisible;
            this.isSectionVisible = visibility >= 0.2;
            if (wasVisible !== this.isSectionVisible) this.manageAutoplay();
        });
      }, { passive: true });
    }

    setupInfiniteLoop() {
      const firstClones = this.items.map(item => item.cloneNode(true));
      const lastClones = this.items.map(item => item.cloneNode(true));
      firstClones.forEach(clone => this.grid.appendChild(clone));
      lastClones.reverse().forEach(clone => this.grid.prepend(clone));

      const itemWidth = this.items[0].offsetWidth + 15;
      const totalWidth = itemWidth * this.items.length;
      this.grid.scrollLeft = totalWidth;

      this.grid.addEventListener('scroll', () => {
        const scrollLeft = this.grid.scrollLeft;
        if (scrollLeft >= totalWidth * 2) {
          this.grid.style.scrollBehavior = 'auto';
          this.grid.scrollLeft = scrollLeft - totalWidth;
        } else if (scrollLeft <= itemWidth) {
          this.grid.style.scrollBehavior = 'auto';
          this.grid.scrollLeft = scrollLeft + totalWidth;
        }
      }, { passive: true });
    }

    setupNavigation() {
      const prevBtn = this.querySelector('.slider-nav-button.prev');
      const nextBtn = this.querySelector('.slider-nav-button.next');
      if (!prevBtn || !nextBtn) return;

      const handleNav = (direction) => {
        const firstItem = this.grid.querySelector('.short-video-item');
        if (!firstItem) return;
        const itemWidth = firstItem.offsetWidth + 15;
        this.grid.style.scrollBehavior = 'smooth';
        this.grid.scrollBy({ left: itemWidth * direction });
      };

      prevBtn.addEventListener('click', (e) => { e.preventDefault(); handleNav(-1); });
      nextBtn.addEventListener('click', (e) => { e.preventDefault(); handleNav(1); });
    }

    setupMuteButtons() {
      // Carousel: mute/unmute nos previews inline
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('.inline-mute-button');
        if (!btn) return;
        e.preventDefault(); e.stopPropagation();
        this.isMuted = !this.isMuted;
        this.querySelectorAll('.short-video-preview').forEach(v => v.muted = this.isMuted);
        this.querySelectorAll('.inline-mute-button').forEach(b => {
          b.classList.toggle('is-unmuted', !this.isMuted);
        });
      });

      // Modal: mute/unmute apenas no vídeo ativo
      if (this.modal) {
        this.modal.addEventListener('click', (e) => {
          const btn = e.target.closest('.video-mute-unmute');
          if (!btn) return;
          e.preventDefault(); e.stopPropagation();
          this.isMuted = !this.isMuted;
          const activeVideo = btn.closest('.short-video-slide')?.querySelector('video');
          if (activeVideo) activeVideo.muted = this.isMuted;
          this.modal.querySelectorAll('.video-mute-unmute').forEach(b => {
            b.classList.toggle('is-unmuted', !this.isMuted);
          });
        });
      }
    }

    setupModal() {
      document.body.appendChild(this.modal);
      this.slides = Array.from(this.modal.querySelectorAll('.short-video-slide'));
      this.viewport = this.modal.querySelector('.short-video-modal-viewport');
      
      this.items.forEach((item, index) => {
        item.addEventListener('click', () => this.openModal(index));
      });

      this.modal.querySelector('.short-video-modal-close')?.addEventListener('click', () => this.closeModal());

      // Setas discretas clicáveis -> próximo / anterior vídeo
      this.nextHint = this.modal.querySelector('.short-video-next-hint');
      this.prevHint = this.modal.querySelector('.short-video-prev-hint');
      this.nextHint?.addEventListener('click', () => this.goToSlide(this.currentIndex + 1));
      this.prevHint?.addEventListener('click', () => this.goToSlide(this.currentIndex - 1));

      // Navegação por teclado (apenas com o modal aberto)
      document.addEventListener('keydown', (e) => {
        if (!this.modal.classList.contains('is-open')) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); this.goToSlide(this.currentIndex + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); this.goToSlide(this.currentIndex - 1); }
        else if (e.key === 'Escape') { this.closeModal(); }
      });

      this.setupProgressSeek();
      this.setupPlayToggle();

      const modalObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) this.handleSlideChange(parseInt(entry.target.dataset.index));
          else entry.target.querySelector('video')?.pause();
        });
      }, { threshold: 0.6, root: this.viewport });

      this.slides.forEach(slide => modalObserver.observe(slide));
    }

    goToSlide(index) {
      if (!this.slides || index < 0 || index >= this.slides.length) return;
      this.viewport.scrollTo({ top: this.slides[index].offsetTop, behavior: 'smooth' });
    }

    setupProgressSeek() {
      this.isScrubbing = false;
      let activeContainer = null;
      let activeVideo = null;
      let wasPlaying = false;

      const ratioFromEvent = (container, clientX) => {
        const rect = container.getBoundingClientRect();
        return Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
      };

      const seekTo = (ratio) => {
        if (!activeVideo || !activeVideo.duration) return;
        activeVideo.currentTime = ratio * activeVideo.duration;
        const bar = activeContainer.querySelector('.short-video-progress-bar');
        const thumb = activeContainer.querySelector('.short-video-progress-thumb');
        if (bar) bar.style.transform = `scaleX(${ratio})`;
        if (thumb) thumb.style.left = `${ratio * 100}%`;
      };

      this.modal.addEventListener('pointerdown', (e) => {
        const container = e.target.closest('.short-video-progress-container');
        if (!container) return;
        e.preventDefault();
        this.isScrubbing = true;
        activeContainer = container;
        activeVideo = container.closest('.short-video-slide')?.querySelector('video');
        wasPlaying = activeVideo && !activeVideo.paused;
        activeVideo?.pause();
        container.classList.add('is-scrubbing');
        container.setPointerCapture?.(e.pointerId);
        seekTo(ratioFromEvent(container, e.clientX));
      });

      this.modal.addEventListener('pointermove', (e) => {
        if (!this.isScrubbing || !activeContainer) return;
        seekTo(ratioFromEvent(activeContainer, e.clientX));
      });

      const endScrub = () => {
        if (!this.isScrubbing) return;
        this.isScrubbing = false;
        activeContainer?.classList.remove('is-scrubbing');
        if (wasPlaying) activeVideo?.play().catch(() => {});
        activeContainer = null;
        activeVideo = null;
      };

      this.modal.addEventListener('pointerup', endScrub);
      this.modal.addEventListener('pointercancel', endScrub);
    }

    setupPlayToggle() {
      this.modal.addEventListener('click', (e) => {
        // Ignora cliques nos controles (som, fechar, barra de progresso)
        if (
          e.target.closest('.short-video-controls-side') ||
          e.target.closest('.short-video-modal-close') ||
          e.target.closest('.short-video-progress-container')
        ) return;

        const video = e.target.closest('.short-video-slide')?.querySelector('video');
        if (!video) return;
        if (video.paused) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
    }

    openModal(index) {
      this.isMuted = false; // Abre desmutado no produto
      this.modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      const targetSlide = this.slides[index];
      this.viewport.scrollTo({ top: targetSlide.offsetTop, behavior: 'auto' });
      this.handleSlideChange(index);
      this.modal.querySelectorAll('.video-mute-unmute').forEach(b => b.classList.add('is-unmuted'));
    }

    closeModal() {
      if (this.progressAnimationFrame) cancelAnimationFrame(this.progressAnimationFrame);
      this.modal.classList.remove('is-open');
      document.body.style.overflow = '';
      // Para e reseta todos os vídeos do modal
      this.modal.querySelectorAll('video').forEach(v => { v.pause(); v.muted = true; v.currentTime = 0; });
      // Reseta estado de mute e garante previews mutados
      this.isMuted = true;
      this.querySelectorAll('.short-video-preview').forEach(v => v.muted = true);
      this.modal.querySelectorAll('.video-mute-unmute').forEach(b => b.classList.remove('is-unmuted'));
    }

    handleSlideChange(index) {
      if (!this.modal.classList.contains('is-open')) return;
      this.currentIndex = index;
      const isLast = index >= this.slides.length - 1;
      // No último vídeo: esconde a seta de "próximo" (baixo) e mostra a de "voltar" (topo)
      this.nextHint?.classList.toggle('is-hidden', isLast);
      this.prevHint?.classList.toggle('is-hidden', !isLast);
      if (this.progressAnimationFrame) cancelAnimationFrame(this.progressAnimationFrame);
      // Pausa e muta todos os outros vídeos antes de tocar o atual
      this.slides.forEach((s, i) => {
        if (i !== index) {
          const v = s.querySelector('video');
          if (v) { v.pause(); v.muted = true; }
        }
      });
      const slide = this.slides[index];
      const video = slide.querySelector('video');
      const progressBar = slide.querySelector('.short-video-progress-bar');
      const progressThumb = slide.querySelector('.short-video-progress-thumb');
      if (video) {
        if (!video.src) video.src = video.dataset.src;
        this.setupLoadingIndicators(video);
        video.muted = this.isMuted;
        video.volume = this.defaultVolume;
        const update = () => {
          if (video.duration && !this.isScrubbing) {
            const progress = video.currentTime / video.duration;
            if (progressBar) progressBar.style.transform = `scaleX(${progress})`;
            if (progressThumb) progressThumb.style.left = `${progress * 100}%`;
          }
          this.progressAnimationFrame = requestAnimationFrame(update);
        };
        this.progressAnimationFrame = requestAnimationFrame(update);
        video.play().catch(() => {
          // Fallback: navegadores mobile bloqueiam autoplay desmutado
          video.muted = true;
          this.isMuted = true;
          this.modal.querySelectorAll('.video-mute-unmute').forEach(b => b.classList.remove('is-unmuted'));
          video.play().catch(() => {});
        });
      }
    }
  }
)}
